// 열람 카드 목록 — **상태는 문구가 아니라 톤이다**
//
// ── 카드 한 장이 말하는 셋 ──────────────────────────────────────────────────
//
// main 착탄 대비 간격, 자기 개시까지 남은 시간, 그리고 착탄 절대 시각. 셋의 성격이 서로
// 다르고 그 차이가 화면에 드러나야 한다.
//
//   간격      시각과 무관한 구조값. 언제 보든 같다.
//   개시까지  **흐른다.** 그리고 dispatch 후이면서 개시 전일 때만 존재한다.
//   착탄      **고정된 절대 시각이고 카운트하지 않는다.** 카운트하면 불통이다.
//
// 착탄을 카운트하지 않는 이유는 그것이 사람이 누를 순간이 아니기 때문이다. 흐르는 숫자를
// 둘 두면 눈이 어느 쪽을 봐야 하는지 화면이 말하지 않게 되고, 급한 순간에 잘못된 쪽을 본다.
//
// ── 왜 문구 배지를 안 쓰는가 ────────────────────────────────────────────────
//
// `SET`·`MARCH`·`HIT` 같은 상태 문구가 발견되면 불통이다. 문구는 읽어야 알고, 읽는 데 드는
// 시간이 이 화면에서는 그대로 오차가 된다. 대신 톤으로 말한다. 단계는 넷이다(`phase.ts`, 2026-10-14
// 사용자). 개시 전 카드는 머리가 타입색으로 물들고 개시까지 남은 시간이 흐른다. 개시가 지나 rallying
// 중인 카드는 그 물들임을 걷고, 개시 정보는 흐르는 숫자 대신 개시한 시각 한 칸으로 낮춘다. 면은 개시
// 전과 같은 raised 라 꺼진 줄로 보이지 않고, 이름, offset, 착탄, 듣기와 눈으로 따라가기는 그대로다.
// 행군에 들어간 카드는 명도가 오르고 진행 바가 붙어 **출발부터 얼마나 왔는지가 길이로** 보이고,
// 착탄이 끝난 카드는 흐려져 정렬과 무관하게 목록 끝으로 가라앉는다. 가라앉히는 이유는 끝난 줄이
// 위에 남아 있으면 눈이 매번 그것을 건너뛰어야 하기 때문이다. 개시가 지났다는 것만으로는 줄 순서도,
// 고른 것도, 버튼도 바뀌지 않는다.
//
// 한국어 「행군 중」은 `MARCH` 의 번역이라 배지로 쓰지 않는다. 진행 바에 붙는 라벨은 상태
// 이름이 아니라 **그 막대가 무엇을 재는지**를 적는다.

import { useRef } from 'react'

import { duration, instant } from '../../../core/time/brand.ts'
import type { RallyKind } from '../../../core/timing/attack.ts'
import { formatImpactClock, formatSignedOffset } from '../../../core/timing/format.ts'
import { common as commonStrings, view as viewStrings, type Lang } from '../../../i18n/index.ts'
import { kindLabel, kindMark } from '../../ui/rally-kind.ts'
import { formatRemaining } from './live-clock.ts'
import { Icon } from '../../ui/icons.tsx'
import { LabelHint } from '../../ui/label-hint.tsx'
import { marchProgress, marchStartOf, phaseOf as phaseAt, type Phase, type PhaseTimes } from './phase.ts'
import {
  burstElapsed,
  burstKey,
  gateBurst,
  lookOf,
  pruneBurstMemo,
  usePageHidden,
  type BurstMemo,
} from './meteor.ts'
import { MeteorGauge } from './meteor-gauge.tsx'
import type { ViewRow } from './use-view-sequence.ts'

export type SortAxis = 'open' | 'impact'

// 타입 문자 표가 여기 있었다. 구성 화면의 것과 **글자 하나까지 같은 표**를 두 파일에 각각
// 적어 두고 「컴포넌트는 공유하지 않는다」를 근거로 삼았는데, 공유하지 않기로 한 것은
// 컴포넌트이고 글자가 아니다. 지금은 공용 네임스페이스가 지고 `ui/rally-kind.ts` 가 꺼낸다.

/**
 * 타입 색 셋. **정본이 값까지 원문으로 못박은 자리다** — main 황금 · strike 마젠타 ·
 * garrison 초록.
 *
 * ── 색과 톤은 서로 다른 축이다 ─────────────────────────────────────────────
 *
 * 이 카드가 말해야 하는 것이 둘인데 한동안 하나만 실려 있었다. **타입은 테두리 색**이고
 * **단계는 명도**다(정본의 「활성 강조 + 완료 침강」). 처음 지을 때 테두리를 단계에 내주는
 * 바람에 타입이 갈 자리가 없어졌고, 세 카드의 테두리와 배지가 전부 같은 흰색이 되어
 * **갈리는 것이 M/S/G 글자 하나뿐**이었다. 2026-08-14 대리 관측이 그것을 적었다.
 *
 * 그래서 축을 다시 가른다 — 색조는 타입에서, 투명도와 배경은 단계에서 온다. 둘이 같은
 * 속성을 다투지 않으므로 한쪽이 다른 쪽을 덮지 않는다.
 *
 * ── 색 단독에 의존하지 않는다 ──────────────────────────────────────────────
 *
 * 정본이 색과 함께 「M/S/G 문자 병기」를 계약했다. 색을 넣는 것과 문자를 빼는 것은 다른
 * 일이고, 문자를 빼는 순간 색맹인 사람에게 타입이 사라진다. 아래에서 글자는 그대로 두고
 * 색만 더한다.
 *
 * 문자열을 조립하지 않고 미리 적어 두는 것은 이 저장소의 규약이다. Tailwind 는 소스에
 * 그대로 적힌 class 이름만 걷어 가므로 만들어 낸 이름은 만들어진 CSS 에 아예 없고, 붙는
 * 자리가 색이라 안 붙어도 오류 없이 조용히 어긋난다.
 */
const KIND_TONE: Record<RallyKind, { border: string; mark: string }> = {
  main: { border: 'border-[#E8C35C] kind-main', mark: 'kind-tile' },
  strike: { border: 'border-[#FF5C8A] kind-strike', mark: 'kind-tile' },
  garrison: { border: 'border-[#5CCB8F] kind-garrison', mark: 'kind-tile' },
}

/**
 * 단계가 정하는 면. **테두리 색은 여기서 정하지 않는다** — 그 자리는 타입의 것이다.
 *
 * ── 3축이 이 표를 면 셋으로 다시 썼다 (2026-08-27) ─────────────────────────
 *
 * 전에는 `before: ''`, `flying: 'bg-white/5'`, `landed: 'opacity-70'` 이었다. 셋 중 면을
 * 가진 것은 행군 중 하나뿐이었고, 그 면조차 배경 대비 1.12 였다. 그래서 개시 전과 행군
 * 중을 가르는 것이 그 한 칸이 전부였다.
 *
 * 지금은 셋 다 자기 면을 가진다. **면은 상태, 선은 구조**라는 축의 규칙을 이 표가 그대로
 * 진다. 아직 안 온 줄이 지면 위로 떠 있고(raised), 지금 흐르는 줄이 더 밝아지며 광채를
 * 얻고(live), 끝난 줄은 지면 아래로 가라앉는다(sunk).
 *
 * ── 이 회차가 화면의 마지막 곱셈을 지웠다 ──────────────────────────────────
 *
 * `opacity-70` 이 화면 코드에 남은 마지막 `opacity-*` 였다. 그것을 쓴 이유는 지나간 줄이
 * 타입 테두리까지 함께 물러나야 해서였는데, **그 요구 자체가 축의 규칙과 어긋났다.** 어느
 * 줄이 main 인지 strike 인지는 끝난 뒤에도 참이라 구조이지 상태가 아니다. 그래서 지금은
 * 테두리를 그대로 두고 면만 가라앉힌다.
 *
 * 그 대신 **이 카드 안에서 잉크 사다리를 쓸 수 있는 범위가 면마다 다르다.** `live` 면 위
 * (1.29)에서 `text-ink-off` 는 3.93 으로 바닥을 깬다. `sunk` 면 위에서는 반대로 off 가
 * 5.39 라 안전하고, 그래서 끝난 줄의 글자를 거기서 한 칸 내린다. 어기면 브라우저 검사가
 * 잡는다.
 *
 * ── rallying 은 raised 면 그대로다 (2026-10-14) ───────────────────────────────
 *
 * 개시가 지났다는 것은 꺼졌다는 뜻이 아니다(사용자). 그래서 면을 내리지 않는다. 내리면 sunk 의 착탄 뒤
 * 줄과 같은 말을 하게 된다. 개시 전과 가르는 것은 개시 쪽 강조를 걷는 일 둘이다: 머리의 타입색 물들임
 * (`rally-head-before`)과 흐르는 개시까지 숫자. raised 위 잉크는 세 칸이라(ink-off 4.16 금지) 낮춘 개시
 * 정보는 ink-mute 다.
 */
const PHASE_TONE: Record<Phase, string> = {
  before: 'bg-surface-raised',
  rallying: 'bg-surface-raised',
  marching: 'bg-surface-live shadow-live',
  impacted: 'bg-surface-sunk text-ink-off',
}

/**
 * 카드 이름 줄의 배경. 위 단계 톤의 면과 같은 불투명 색이다. 이름 줄이 카드 안에서 고정될 때 아래 줄이 비쳐 보이지 않게
 * 하려는 것이다(T6 개정 셋째, 아래 `view-card-head` 주석). 타입색 물들임은 개시 전에만 있다.
 */
const PHASE_BG: Record<Phase, string> = {
  before: 'bg-surface-raised rally-head-before',
  rallying: 'bg-surface-raised',
  marching: 'bg-surface-live',
  impacted: 'bg-surface-sunk',
}

/** 줄 하나에서 단계를 가르는 시각 셋을 꺼낸다. 행군 시간은 dispatch 때 얼어붙은 그 줄의 값이다. */
function timesOf(row: ViewRow): PhaseTimes {
  return { openMs: row.openMs, impactMs: row.impactMs, marchingMs: row.rally.marchingMs }
}

/** 지금이 어느 구간인가. **저장하지 않고 매번 파생시킨다.** 시각이 흐르면 답이 달라진다(`phase.ts`). */
function phaseOf(row: ViewRow, nowMs: number): Phase {
  return phaseAt(timesOf(row), nowMs)
}

/**
 * 착탄 시각을 0.1초 칸에 맞춰 적는다.
 *
 * 표시 형식이 0.1초 해상도라 그보다 잔 자리는 적을 수 없고, 적으려 하면 형식 함수가 거절한다.
 * 실제 앵커는 `지금 + 손잡이` 로 나온 임의의 밀리초라 100 의 배수가 아니다. **버리지 않고
 * 반올림하는 이유**는, 내림이 아직 안 온 착탄을 이미 온 것처럼 당겨 적을 수 있기 때문이다.
 */
function impactClock(millis: number): string {
  return formatImpactClock(instant(Math.round(millis / 100) * 100))
}

export interface CardsProps {
  lang: Lang
  rows: readonly ViewRow[]
  nowMs: number
  axis: SortAxis
  onAxisChange: (axis: SortAxis) => void
  /** 소리를 내고 있는 줄. 없으면 `null`. */
  signalRallyId: string | null
  highlighted: readonly string[]
  onToggleSignal: (rallyId: string) => void
  onToggleHighlight: (rallyId: string) => void
  /**
   * 기기의 움직임 줄이기. 행군 막대 메테오(`meteor.ts`, 사용자 2026-10-14 채택)가 머리와 멈춘 빛 겹 하나만 남기고 다른 장식, 착탄
   * 확산, 침강 늦춤이 없다. 없으면 꺼짐이다.
   */
  reducedMotion?: boolean
  /**
   * 이 목록이 마운트된 채 감춰져 있다(열람 화면이 defense 탭일 때 attack 칸). 그 동안 착탄한 줄은 메테오 확산 없이 곧바로
   * 침강한다(`meteor.ts` 의 `gateBurst`).
   */
  concealed?: boolean
}

export function Cards(props: CardsProps): React.ReactElement {
  const t = viewStrings(props.lang)
  const c = commonStrings(props.lang)
  const reduced = props.reducedMotion ?? false

  // 메테오 착탄 확산이 보이는 동안(0.9초)만 착탄 뒤 줄이 행군 모양과 제자리를 지킨다(`meteor.ts`, 사용자 2026-10-14 「0.9초 늦춤
  // 유지」). 움직임 줄이기면 확산이 없어 화면 모양의 단계가 언제나 시각 그대로의 단계와 같다.
  //
  // 확산은 착탄 하나에 한 번만 열린다. 그 기억은 이 목록이 떠 있는 동안 이어지고(새로고침이나 재접속이면 새로 시작하지만 그때는 경과만큼
  // 음수 지연으로 이어 그리므로 처음부터 터지지 않는다), 그리기마다 줄 하나에 한 번만 묻는다. 움직임 줄이기에서는 기억에 아무것도 적히지 않는다.
  const memo = useRef<BurstMemo>(new Map()).current
  const pageHidden = usePageHidden()
  const unseen = pageHidden || (props.concealed ?? false)
  const bursts = new Map<string, number | null>()
  const liveKeys = new Set<string>()
  for (const row of props.rows) {
    const key = burstKey(row.rally.id, row.impactMs)
    liveKeys.add(key)
    // 행군 구간이 있었던 줄만 끝점이 있다. 행군 시간 0 인 줄은 rallying 에서 곧바로 착탄 뒤로 가서 막대가 한 번도 없었다.
    const marched = row.impactMs > marchStartOf(timesOf(row))
    const raw = marched ? burstElapsed(row.impactMs, props.nowMs, reduced) : null
    bursts.set(row.rally.id, gateBurst(memo, key, raw, unseen))
  }
  pruneBurstMemo(memo, liveKeys)
  const burstOf = (row: ViewRow): number | null => bursts.get(row.rally.id) ?? null
  const lookOfRow = (row: ViewRow): Phase => lookOf(phaseOf(row, props.nowMs), burstOf(row))

  // **착탄이 끝난 줄은 정렬 축과 무관하게 끝으로 간다.** 축 안에서만 정렬하면 이미 끝난
  // 줄이 목록 한가운데 남아, 눈이 살아 있는 줄을 찾을 때마다 그것을 건너뛴다.
  const sorted = [...props.rows].sort((a, b) => {
    // 착탄 뒤인지만 본다. 개시가 지났다는 것(rallying, 행군)은 순서를 바꾸지 않는다(2026-10-14 사용자).
    const sunk = Number(lookOfRow(a) === 'impacted') - Number(lookOfRow(b) === 'impacted')
    if (sunk !== 0) return sunk
    return props.axis === 'open' ? a.openMs - b.openMs : a.impactMs - b.impactMs
  })

  // 칸 안은 라벨 위, 뜻풀이 아래 두 요소다(2026-10-14 사용자 지시, 대시로 잇지 않는다). 구성 화면과 같은 키를 읽는다.
  const axes = [
    ['open', c.sortByOpen, c.sortByOpenHint],
    ['impact', c.sortByImpact, c.sortByImpactHint],
  ] as const

  return (
    <section aria-label={t.listTitle} className="mt-block">
      {/* c2: 640 미만에서는 「정렬」 라벨과 줄 수를 위 한 줄(작은 글자)에, 두 칸 묶음을 아래 줄 전체 폭에 둔다. 칸 글자가 접히지 않아 칸 높이가
          44px 로 선다(c 는 두 줄 50px). 요소 순서(라벨, 묶음, 줄 수)와 글자는 그대로이고 배치만 격자다. 640 이상은 한 줄 그대로. */}
      <div
        className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 gap-y-1.5 sm:flex sm:flex-wrap sm:gap-2"
        data-testid="view-sort-toggle"
      >
        <span className="col-start-1 row-start-1 text-xs font-medium text-ink-dim sm:text-sm sm:font-normal">{c.sortLabel}</span>
        {/* 장식 묶음(감싸는 span): 두 칸을 한 홈 안에 세운다. 순서와 글자는 그대로다. */}
        <span className="seg-group seg-group-fill col-span-2 col-start-1 row-start-2">
        {axes.map(([axis, label, hint]) => (
          <button
            key={axis}
            type="button"
            data-testid={`view-sort-${axis}`}
            data-active={String(props.axis === axis)}
            // **눌린 상태를 기계에도 알린다.** 눈으로는 테두리 색으로 갈리지만, 화면을
            // 소리로 읽는 사람에게는 지금 어느 쪽으로 정렬돼 있는지 전달되지 않았다.
            // 구성 화면의 같은 컨트롤은 처음부터 이 속성을 달고 있었다 — 같은 제품 안의
            // 같은 버튼이 두 화면에서 다르게 만들어져 있던 자리다. (2026-08-14 관측.)
            aria-pressed={props.axis === axis}
            className="seg"
            onClick={() => {
              props.onAxisChange(axis)
            }}
          >
            <LabelHint label={label} hint={hint} stack hintClassName="text-xs font-normal text-ink-mute" />
          </button>
        ))}
        </span>
        <span className="col-start-2 row-start-1 text-xs text-ink-dim sm:ml-auto">
          {t.listCount.replaceAll('{n}', String(props.rows.length))}
        </span>
      </div>

      <ul className="mt-group grid gap-2" data-testid="view-cards">
        {sorted.map((row) => {
          const kind = row.rally.type as RallyKind
          const phase = phaseOf(row, props.nowMs)
          // 면, 막대, 머리 줄 배경은 화면 모양의 단계를 따른다. `data-phase` 는 언제나 시각 그대로의 단계다.
          const burst = burstOf(row)
          const look = lookOf(phase, burst)
          const listening = props.signalRallyId === row.rally.id
          const marked = props.highlighted.includes(row.rally.id)
          // 행군 막대는 출발부터 착탄까지만 잰다. rallying 동안은 아무것도 출발하지 않았다.
          const flown = marchProgress(timesOf(row), props.nowMs)

          return (
            <li
              key={row.rally.id}
              data-testid="view-card"
              data-rally-id={row.rally.id}
              data-type={kind}
              data-phase={phase}
              // 메테오 착탄 확산이 보이는 동안(0.9초)만 붙는다. 그 동안 data-phase 는 impacted 인데 모양은 행군이다(CSS 가 이 표식으로 계기 줄의 잉크를 행군 쪽에 둔다).
              data-meteor-burst={burst !== null ? 'true' : undefined}
              data-listening={String(listening)}
              data-highlighted={String(marked)}
              // **테두리 색은 타입, 명도는 단계.** 두 축이 같은 속성을 다투지 않으므로
              // 한쪽이 다른 쪽을 덮지 않는다 — 행군에 든 줄은 밝아지고 끝난 줄은 흐려지되,
              // 그 줄이 main 인지 strike 인지는 어느 단계에서도 테두리 색으로 남는다.
              className={`card rally-card text-sm ${
                KIND_TONE[kind].border
              } ${PHASE_TONE[look]} ${marked ? 'ring-2 ring-cyan' : ''}`}
            >
              {/* **이름 줄은 그 카드 안에서 상단 고정줄과 신호 존 머리 바로 아래에 붙는다** (T6 개정 셋째, 2026-10-05
                  사용자). 카드가 고정된 부분 밑으로 반쯤 들어가면 이름은 숨고 「개시까지 T−」만 남아 남의 숫자로 읽혔다
                  (대리 관측, 360 폭). 카드 안에서만 붙으므로 카드가 다 지나가면 함께 사라진다. grid 칸 안의 sticky 는 그
                  칸 안에서만 움직여 효과가 없어서, 카드를 이름 줄과 grid 몸 둘로 나눴다. */}
              <div
                data-testid="view-card-head"
                className={`rally-head sticky top-[calc(var(--topbar-h,0px)+var(--signal-head-h,0px))] z-[4] grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 ${PHASE_BG[look]}`}
              >
              <span className="col-start-1 row-start-1 flex min-w-0 items-center gap-2.5">
                {/* **글자는 그대로 두고 색만 더한다.** 색을 넣는 것과 문자를 빼는 것은
                    다른 일이고, 문자를 빼는 순간 색맹인 사람에게 타입이 사라진다. */}
                <span
                  data-testid="view-card-kind"
                  className={`font-mono ${KIND_TONE[kind].mark}`}
                  aria-label={kindLabel(kind, c)}
                >
                  {kindMark(kind, c)}
                </span>
                <span data-testid="view-card-roster" className="min-w-0 text-base font-semibold">
                  {row.rosterName}
                </span>
              </span>

              <button
                type="button"
                data-testid="view-card-listen"
                // 버튼이 줄마다 같은 글자로 여럿 뜬다. 낭독기에는 그 줄이 누구의 줄인지가
                // 안 실리므로 이름을 붙여 가른다. 명부와 미니 시퀀스가 같은 규칙을 쓴다.
                aria-label={`${row.rosterName} ${listening ? t.listeningLabel : t.listenLabel}`}
                aria-pressed={listening}
                className={`col-start-2 row-start-1 shrink-0 ${listening ? 'chip-hit' : 'btn btn-secondary'}`}
                onClick={() => {
                  props.onToggleSignal(row.rally.id)
                }}
              >
                {listening ? (
                  <span className="chip chip-accent chip-dot">{t.listeningLabel}</span>
                ) : (
                  <>
                    <Icon name="listen" />
                    {t.listenLabel}
                  </>
                )}
              </button>
              </div>

              <div className="mt-line grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 gap-y-1">

              <span className="metrics col-span-2 col-start-1 row-start-1">
                <span className="metric">
                  <span className="metric-label">
                    {t.columnOffset}
                  </span>
                  <span data-testid="view-card-offset" className="metric-value font-mono">
                    {formatSignedOffset(duration(row.rally.offsetMs))}
                  </span>
                </span>

                {/* **개시까지는 dispatch 후이면서 개시 전일 때만 존재한다.** 지난 뒤에도
                    남겨 두면 음수가 흐르고, 그 숫자는 아직 누를 수 있다는 인상을 준다. */}
                {phase === 'before' && (
                  <span className="metric">
                    <span className="metric-label">
                      {t.columnOpen}
                    </span>
                    <span data-testid="view-card-open" className="metric-value font-mono">
                      {formatRemaining(row.openMs - props.nowMs)}
                    </span>
                  </span>
                )}

                {/* 개시가 지나 rallying 인 동안은 개시한 시각을 고정값 한 칸으로 남긴다(2026-10-14 사용자 지시, 모양은 세션
                    선택). 흐르는 숫자를 걷어 「아직 누를 것」이라는 인상을 없애고, 라벨과 값을 ink-mute 와 보통 굵기로 내려
                    이름, offset, 착탄보다 한 칸 물러나게 한다. 행군이 시작되면 이 칸도 사라지고 막대가 그 자리를 말한다. */}
                {phase === 'rallying' && (
                  <span className="metric">
                    <span className="metric-label text-ink-mute">
                      {t.columnOpened}
                    </span>
                    <span data-testid="view-card-opened" className="metric-value font-mono font-normal text-ink-mute">
                      {impactClock(row.openMs)}
                    </span>
                  </span>
                )}

                <span className="metric">
                  <span className="metric-label">
                    {t.columnImpact}
                  </span>
                  {/* 고정값이다. 흐르지 않는다. */}
                  <span data-testid="view-card-impact" className="metric-value font-mono">
                    {impactClock(row.impactMs)}
                  </span>
                </span>
              </span>

              {/* 행군에 든 줄에만 붙는다(착탄 확산 동안 포함). 출발부터 얼마나 왔는지를 길이로 말하므로 읽지 않아도 보이고, 그 끝에
                  메테오가 달린다(`meteor-gauge.tsx`, 사용자 2026-10-14 「매우 강함 채택.」). 채움은 오렌지다(c2). 오렌지가 「지금」의
                  색이고 막대는 지금 흐르는 것을 잰다. 타입은 카드 테두리와 타입 타일이 이미 말하고 있어 막대까지 타입색이면 같은 뜻을
                  세 번 말한다(c 의 반증 검사가 짚은 메모 밖 변경). */}
              {look === 'marching' && (
                <MeteorGauge reduced={reduced} progress={flown} burstElapsedMs={burst} label={t.progressLabel} />
              )}

              <button
                type="button"
                data-testid="view-card-highlight"
                aria-label={`${row.rosterName} ${marked ? t.highlightOnLabel : t.highlightLabel}`}
                aria-pressed={marked}
                className={`col-span-2 col-start-1 row-start-3 -ml-2 justify-self-start ${marked ? 'chip-hit' : 'btn btn-ghost'}`}
                onClick={() => {
                  props.onToggleHighlight(row.rally.id)
                }}
              >
                {marked ? (
                  <span className="chip chip-cyan chip-dot">{t.highlightOnLabel}</span>
                ) : (
                  <>
                    <Icon name="eye" />
                    {t.highlightLabel}
                  </>
                )}
              </button>
              </div>
            </li>
          )
        })}
      </ul>
    </section>
  )
}
